/* ============================================================
   Админка · Новости: таблица + редактор поста
   (заголовок, описание, текст, категория, дата, обложка).

   Обложка — картинка (грузится на сервер) либо, если картинки нет,
   цветной градиент по hue. Статус в таблице реальный: пост можно
   отложить в черновики, и на витрине его не будет.
   ============================================================ */
import { useState } from 'react';
import { useFedContent, uploadUrl, isPublished } from '../content';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { AdmCard, hueCover, AdmIconBtn, AdmPrimaryBtn, AdmImagePicker, EmptyRow, ViewOnSiteBtn } from './ui';

const CATS = [
  { ru: 'Турниры', kz: 'Турнирлер' },
  { ru: 'Анонсы', kz: 'Анонстар' },
  { ru: 'Федерация', kz: 'Федерация' },
  { ru: 'Обучение', kz: 'Оқыту' },
  { ru: 'Медиа', kz: 'Медиа' },
];

/** Мини-превью обложки: картинка, если загружена, иначе градиент по hue. */
function CoverThumb({ item, size = 40 }) {
  return (
    <span className="fed-news-cover rounded-lg flex-shrink-0 border border-line-2 overflow-hidden" style={{ width: size, height: size * 0.8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: hueCover(item.hue ?? 8) }}>
      {item.cover
        ? <img src={uploadUrl(item.cover)} alt="" className="w-full h-full" style={{ objectFit: 'cover' }} />
        : <Ic name="news" size={24} color="var(--fed-accent-bright)" />}
    </span>
  );
}

export function AdmNewsList({ onEdit }) {
  const news = useFedContent((s) => s.news);
  const deleteNews = useFedContent((s) => s.deleteNews);
  const fire = useAppStore((s) => s.fire);

  const remove = async (n) => {
    if (!window.confirm(`Удалить пост «${n.title_ru}»?`)) return;
    const ok = await deleteNews(n.id);
    if (ok) fire && fire('Пост удалён', 'ok');
  };

  return (
    <div className="fed-admin-view fed-news-list">
      <AdmCard className="fed-news-list-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="fed-tbl">
            <thead>
              <tr><th>Заголовок</th><th>Категория</th><th>Дата</th><th>Статус</th><th>Действия</th></tr>
            </thead>
            <tbody>
              {news.map((n) => {
                const live = isPublished(n);
                return (
                  <tr key={n.id} className="fed-list-showcase-card fed-news-showcase-card">
                    <td data-wide className="fed-news-title-cell fed-showcase-hero-cell">
                      <span className="fed-showcase-menu" aria-hidden="true"><Ic name="more" size={19} /></span>
                      <div className="fed-news-title fed-showcase-hero flex items-center gap-3">
                        <CoverThumb item={n} />
                        <div className="min-w-0">
                          <div className="fed-showcase-title font-semibold text-fg" style={{ fontSize: 14, maxWidth: 380 }}>{n.title_ru}</div>
                          <div className="fed-showcase-subtitle text-fg-faint">{n.ex_ru || n.cat_ru}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Категория"><span className="font-mono text-gold bg-surface-3 rounded-md" style={{ fontSize: 11, padding: '4px 8px' }}>{n.cat_ru}</span></td>
                    <td data-label="Дата" className="font-mono" style={{ whiteSpace: 'nowrap' }}>{n.date}</td>
                    <td data-label="Статус">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full font-mono uppercase"
                        style={{
                          padding: '4px 10px', fontSize: 10, letterSpacing: '.1em',
                          color: live ? 'var(--fed-ok)' : 'var(--fed-gold)',
                          background: live ? 'rgba(67,217,106,.12)' : 'rgba(230,183,60,.12)',
                        }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: live ? 'var(--fed-ok)' : 'var(--fed-gold)' }} />
                        {live ? 'Опубликовано' : 'Черновик'}
                      </span>
                    </td>
                    <td>
                      <div className="fed-news-actions-desktop flex gap-2">
                        <ViewOnSiteBtn route={`article/${n.id}`} title="Смотреть страницу" />
                        <AdmIconBtn name="edit" title="Редактировать" onClick={() => onEdit(n.id)} />
                        <AdmIconBtn name="trash" tint="danger" title="Удалить" onClick={() => remove(n)} />
                      </div>
                      <div className="fed-news-actions-mobile">
                        <a href={`/#article/${n.id}`} target="_blank" rel="noopener noreferrer" className="fed-tour-bigbtn ghost">
                          <Ic name="external" size={17} /> К новости
                        </a>
                        <button onClick={() => onEdit(n.id)} className="fed-tour-bigbtn primary">
                          <Ic name="edit" size={17} /> Изменить
                        </button>
                        <button onClick={() => remove(n)} className="fed-tour-delete-mobile" aria-label={`Удалить новость ${n.title_ru}`}>
                          <Ic name="trash" size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!news.length && <EmptyRow colSpan={5}>Новостей нет</EmptyRow>}
            </tbody>
          </table>
        </div>
      </AdmCard>
    </div>
  );
}

export function AdmNewsEditor({ newsId, onBack }) {
  const news = useFedContent((s) => s.news);
  const saveNews = useFedContent((s) => s.saveNews);
  const fire = useAppStore((s) => s.fire);
  const [busy, setBusy] = useState(false);
  const [editLang, setEditLang] = useState('ru');
  const src = news.find((n) => n.id === newsId);

  const [draft, setDraft] = useState(() =>
    src
      ? {
          published: true, cover: '', ...src,
          bodyText_ru: (src.body_ru || []).join('\n\n'),
          bodyText_kz: (src.body_kz || []).join('\n\n'),
        }
      : {
          id: 'n' + Math.random().toString(36).slice(2, 9),
          cat_ru: 'Турниры', cat_kz: 'Турнирлер',
          date: new Date().toLocaleDateString('ru-RU'),
          hue: 8, cover: '', published: true,
          title_ru: '', title_kz: '', ex_ru: '', ex_kz: '', bodyText_ru: '', bodyText_kz: '',
        }
  );

  const commit = async (published) => {
    if (busy) return;
    if (!draft.title_ru.trim()) { fire && fire('Укажите заголовок', 'error'); return; }
    setBusy(true);
    const paragraphs = (value) => String(value || '').split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
    const bodyRu = paragraphs(draft.bodyText_ru);
    const bodyKz = paragraphs(draft.bodyText_kz);
    const { bodyText_ru, bodyText_kz, ...rest } = draft;
    const ok = await saveNews({
      ...rest,
      published,
      title_kz: rest.title_kz || rest.title_ru,
      ex_kz: rest.ex_kz || rest.ex_ru,
      cat_kz: rest.cat_kz || rest.cat_ru,
      body_ru: bodyRu,
      body_kz: bodyKz.length ? bodyKz : bodyRu,
    });
    setBusy(false);
    // при неудаче остаёмся в редакторе: на сервер пост не ушёл, и уход
    // отсюда означал бы потерю текста на следующей загрузке
    if (ok) { fire && fire(published ? 'Пост опубликован' : 'Черновик сохранён', 'ok'); onBack(); }
  };

  return (
    <div className="fed-admin-view fed-news-editor flex flex-col gap-5">
      <div className="fed-news-editor-head flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="fed-news-back bg-transparent border-none cursor-pointer text-fg-muted font-display font-semibold uppercase hover:text-fg" style={{ fontSize: 13.5, letterSpacing: '.04em' }}>
          ← К списку новостей
        </button>
        <span className="flex-1" />
        {src && (
          <a href={`/#article/${draft.id}`} target="_blank" rel="noopener noreferrer" className="fed-tour-editor-view fed-news-editor-view">
            <Ic name="external" size={17} /> Открыть новость
          </a>
        )}
      </div>

      <div className="flex gap-2" aria-label="Язык новости">
        {[['ru', 'Русский'], ['kz', 'Қазақша']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setEditLang(key)}
            className="h-10 px-4 rounded-[10px] cursor-pointer font-display font-semibold uppercase border"
            style={{
              fontSize: 12.5,
              color: editLang === key ? 'var(--fed-fg)' : 'var(--fed-fg-muted)',
              borderColor: editLang === key ? 'var(--fed-line-accent)' : 'var(--fed-line)',
              background: editLang === key ? 'rgba(225,18,23,.08)' : 'transparent',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="fed-news-editor-grid grid gap-4 items-start" style={{ gridTemplateColumns: '1.6fr 1fr' }}>
        {/* текст */}
        <AdmCard style={{ padding: 24 }} className="fed-news-edit-card flex flex-col gap-4">
          <label className="flex flex-col gap-[7px]">
            <span className="fed-label">{editLang === 'ru' ? 'Заголовок' : 'Тақырып'}</span>
            <input className="fed-input" value={draft[`title_${editLang}`] || ''} onChange={(e) => setDraft({ ...draft, [`title_${editLang}`]: e.target.value })} />
          </label>
          <label className="flex flex-col gap-[7px]">
            <span className="fed-label">{editLang === 'ru' ? 'Краткое описание' : 'Қысқаша сипаттама'}</span>
            <textarea className="fed-input" rows={2} value={draft[`ex_${editLang}`] || ''} onChange={(e) => setDraft({ ...draft, [`ex_${editLang}`]: e.target.value })} />
          </label>
          <label className="flex flex-col gap-[7px]">
            <span className="fed-label">{editLang === 'ru' ? 'Текст статьи (абзацы — через пустую строку)' : 'Мақала мәтіні (абзацтар — бос жол арқылы)'}</span>
            <textarea className="fed-input" rows={10} value={draft[`bodyText_${editLang}`] || ''} onChange={(e) => setDraft({ ...draft, [`bodyText_${editLang}`]: e.target.value })} />
          </label>
        </AdmCard>

        {/* мета */}
        <div className="flex flex-col gap-4">
          <AdmCard style={{ padding: 24 }} className="fed-news-edit-card flex flex-col gap-4">
            <label className="flex flex-col gap-[7px]">
              <span className="fed-label">{editLang === 'ru' ? 'Категория' : 'Санат'}</span>
              <select
                className="fed-input"
                value={draft[`cat_${editLang}`] || ''}
                onChange={(e) => setDraft({ ...draft, [`cat_${editLang}`]: e.target.value })}
              >
                {CATS.map((c) => <option key={c.ru} value={c[editLang]}>{c[editLang]}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-[7px]">
              <span className="fed-label">Дата</span>
              <input className="fed-input" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            </label>

            <AdmImagePicker
              label="Обложка"
              value={draft.cover || ''}
              onChange={(path) => setDraft({ ...draft, cover: path })}
              hint="Показывается в списке новостей и на странице поста."
              preview={{ w: 72, h: 54, radius: 10 }}
            />

            {!draft.cover && (
              <div className="flex flex-col gap-[7px]">
                <span className="fed-label">Цвет обложки (пока нет картинки)</span>
                <input
                  type="range" min={0} max={360} value={draft.hue}
                  onChange={(e) => setDraft({ ...draft, hue: parseInt(e.target.value, 10) })}
                  style={{ accentColor: 'var(--fed-accent)' }}
                />
                <div className="rounded-xl border border-line-2" style={{ height: 90, background: hueCover(draft.hue) }} />
              </div>
            )}
          </AdmCard>

          <AdmCard style={{ padding: 18 }} className="fed-news-publish-card flex flex-col gap-2.5">
            <div className="fed-news-publish-note flex items-center gap-2 text-fg-muted" style={{ fontSize: 12.5 }}>
              <Ic name="news" size={15} />
              {draft.published === false ? 'Сейчас черновик — на витрине не показывается' : 'Будет виден на витрине сразу'}
            </div>
            <AdmPrimaryBtn icon="check" onClick={() => commit(true)} className="fed-news-publish justify-center">
              {busy ? 'Сохраняем…' : 'Опубликовать'}
            </AdmPrimaryBtn>
            <div className="fed-news-secondary-actions flex gap-2.5">
              <button onClick={() => commit(false)} className="fed-news-editor-action fed-btn-ghost h-10 px-4 rounded-[10px] flex-1" style={{ fontSize: 13 }}>
                В черновики
              </button>
              <button onClick={onBack} className="fed-news-editor-action fed-btn-ghost h-10 px-5 rounded-[10px]" style={{ fontSize: 13 }}>Отмена</button>
            </div>
          </AdmCard>
        </div>
      </div>
    </div>
  );
}
